export interface DateTimeImmutable {
  date: string;
  timezone_type: number;
  timezone: string;
}

export const formatDate = (
  dateInput: string | DateTimeImmutable | null,
): string => {
  if (!dateInput) return "";

  try {
    const dateString =
      typeof dateInput === "object" && "date" in dateInput
        ? dateInput.date
        : dateInput;

    const date = new Date(dateString);

    if (isNaN(date.getTime())) {
      return "";
    }

    return new Intl.DateTimeFormat("en-US", {
      month: "2-digit",
      day: "2-digit",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
      timeZone: "UTC",
    })
      .format(date)
      .replace(",", "");
  } catch (error) {
    console.error("Error formatting date:", error, dateInput);
    return "";
  }
};

/**
 * Formats a date range into a human-readable string
 * @param startDate ISO date string for the start date
 * @param endDate ISO date string for the end date
 * @returns Formatted date range string
 */
export const formatDateRange = (startDate: string, endDate: string): string => {
  const start = new Date(startDate);
  const end = new Date(endDate);

  // Format: March 15-17, 2025
  if (
    start.getMonth() === end.getMonth() &&
    start.getFullYear() === end.getFullYear()
  ) {
    return `${start.toLocaleString("default", { month: "long" })} ${start.getDate()}-${end.getDate()}, ${start.getFullYear()}`;
  }

  // Format: March 30 - April 2, 2025
  if (start.getFullYear() === end.getFullYear()) {
    return `${start.toLocaleString("default", { month: "long" })} ${start.getDate()} - ${end.toLocaleString("default", { month: "long" })} ${end.getDate()}, ${start.getFullYear()}`;
  }

  // Format: December 30, 2024 - January 2, 2025
  return `${start.toLocaleString("default", { month: "long" })} ${start.getDate()}, ${start.getFullYear()} - ${end.toLocaleString("default", { month: "long" })} ${end.getDate()}, ${end.getFullYear()}`;
};

/**
 * Formats a time range into a human-readable string with timezone
 * @param startDate ISO date string for the start date
 * @param endDate ISO date string for the end date
 * @returns Formatted time range string with timezone
 */
export const formatTimeRange = (startDate: string, endDate: string): string => {
  const start = new Date(startDate);
  const end = new Date(endDate);

  const startTime = start.toLocaleTimeString("en-US", {
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  });
  const endTime = end.toLocaleTimeString("en-US", {
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  });

  return `${startTime} - ${endTime} EST`;
};

/**
 * Gets the ISO 8601 week number for a given date
 * @param date The date to get the week number for (defaults to current date)
 * @returns The ISO week number (1-53)
 * @example
 * const currentWeek = getISOWeekNumber(); // e.g., 47
 * const specificWeek = getISOWeekNumber(new Date('2024-12-31')); // Returns 1 (first week of 2025)
 */
export const getISOWeekNumber = (date: Date = new Date()): number => {
  const d = new Date(
    Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()),
  );
  const dayNum = d.getUTCDay() || 7; // Make Sunday = 7 instead of 0

  // Set to nearest Thursday: current date + 4 - current day number
  d.setUTCDate(d.getUTCDate() + 4 - dayNum);

  // Get first day of year
  const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));

  // Calculate full weeks between date and first day of year
  const weekNo = Math.ceil(
    ((d.getTime() - yearStart.getTime()) / 86400000 + 1) / 7,
  );

  return weekNo;
};

/**
 * Gets the ISO year for a given date (may differ from calendar year for dates in week 1 or 52/53)
 * @param date The date to get the ISO year for
 * @returns The ISO year
 */
export const getISOYear = (date: Date): number => {
  const d = new Date(
    Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()),
  );
  const dayNum = d.getUTCDay() || 7;

  // Set to nearest Thursday
  d.setUTCDate(d.getUTCDate() + 4 - dayNum);

  return d.getUTCFullYear();
};

/**
 * Gets ISO week number and year for a given date
 * @param date The date to get week info for
 * @returns Object with week number and year
 */
export const getISOWeekInfo = (date: Date): { week: number; year: number } => {
  return {
    week: getISOWeekNumber(date),
    year: getISOYear(date),
  };
};

/**
 * Gets an array of ISO week numbers and years for a range relative to a given date
 * Handles year boundaries correctly (e.g., week 52, 53, 1 across new year)
 * @param weeksBack Number of weeks before the reference date
 * @param weeksForward Number of weeks after the reference date
 * @param referenceDate The reference date (defaults to current date)
 * @returns Object with unique year numbers and week numbers across the range
 * @example
 * // If current date is in week 51 of 2024:
 * const range = getISOWeekRange(3, 2);
 * // Returns: { yearNumbers: [2024, 2025], weekNumbers: [48, 49, 50, 51, 52, 1] }
 */
export const getISOWeekRange = (
  weeksBack: number,
  weeksForward: number,
  referenceDate: Date = new Date(),
): {
  yearNumbers: number[];
  weekNumbers: number[];
  weeks: Array<{ week: number; year: number }>;
} => {
  const weeks: Array<{ week: number; year: number }> = [];

  // Calculate weeks in the range
  for (let i = -weeksBack; i <= weeksForward; i++) {
    const targetDate = new Date(referenceDate);
    targetDate.setDate(targetDate.getDate() + i * 7);

    const weekInfo = getISOWeekInfo(targetDate);
    weeks.push(weekInfo);
  }

  // Extract unique years and week numbers
  const yearSet = new Set<number>();
  const weekSet = new Set<number>();

  weeks.forEach(({ week, year }) => {
    yearSet.add(year);
    weekSet.add(week);
  });

  return {
    yearNumbers: Array.from(yearSet).sort(),
    weekNumbers: Array.from(weekSet).sort((a, b) => a - b),
    weeks, // Return ordered week/year pairs
  };
};

/**
 * Gets the total number of ISO weeks in a given year
 * Most years have 52 weeks, but some have 53
 * @param year The year to check
 * @returns The number of weeks (52 or 53)
 */
export const getWeeksInYear = (year: number): number => {
  // December 28th is always in the last week of the ISO year
  const dec28 = new Date(year, 11, 28);
  return getISOWeekNumber(dec28);
};

/**
 * Calculates the number of days remaining until a due date
 * @param dueDate The due date to calculate from
 * @returns Positive number for future dates, negative for past dates
 */
export const calculateDaysRemaining = (dueDate: Date): number => {
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const due = new Date(
    dueDate.getFullYear(),
    dueDate.getMonth(),
    dueDate.getDate(),
  );

  const diffTime = due.getTime() - today.getTime();
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

  return diffDays;
};

/**
 * Formats a due date into a human-readable string
 * @param dueDate The due date to format (null if no deadline)
 * @returns Formatted string like "Due in 3 days", "Overdue by 2 days", or "No deadline"
 */
export const formatDueDate = (dueDate: Date | null): string => {
  if (!dueDate) {
    return "No deadline";
  }

  const daysRemaining = calculateDaysRemaining(dueDate);

  if (daysRemaining === 0) {
    return "Due today";
  } else if (daysRemaining === 1) {
    return "Due tomorrow";
  } else if (daysRemaining > 1) {
    return `Due in ${daysRemaining} days`;
  } else if (daysRemaining === -1) {
    return "Overdue by 1 day";
  } else {
    return `Overdue by ${Math.abs(daysRemaining)} days`;
  }
};

/**
 * Determines the status of a due date
 * @param dueDate The due date to check (null if no deadline)
 * @returns Status: 'none' | 'upcoming' | 'approaching' | 'overdue'
 */
export const getDueDateStatus = (
  dueDate: Date | null,
): "none" | "upcoming" | "approaching" | "overdue" => {
  if (!dueDate) {
    return "none";
  }

  const daysRemaining = calculateDaysRemaining(dueDate);

  if (daysRemaining < 0) {
    return "overdue";
  } else if (daysRemaining >= 1 && daysRemaining <= 7) {
    return "approaching";
  } else {
    return "upcoming";
  }
};

/**
 * Validates if a date is in the future
 * @param date The date to validate
 * @returns True if the date is in the future, false otherwise
 */
export const isFutureDate = (date: Date): boolean => {
  const now = new Date();
  return date.getTime() > now.getTime();
};
